import React, { useEffect, useMemo, useState } from "react";
import { useApp, t } from "@/lib/AppContext";
import { api, resolveImg } from "@/lib/api";
import CarCard from "@/components/CarCard";
import SectionLabel from "@/components/SectionLabel";
import FeedbackDialog from "@/components/FeedbackDialog";
import { Compass } from "lucide-react";

export default function Configurator() {
  const { content, settings } = useApp();
  const [options, setOptions] = useState([]);
  const [results, setResults] = useState([]);
  const [count, setCount] = useState(0);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [filters, setFilters] = useState({
    brand: "", body_type: "", fuel_type: "", transmission: "", country_origin: "", customs_cleared: "", euro_standard: "",
    price_min: "", price_max: "",
  });

  useEffect(() => { api.get("/configurator-options").then(r => setOptions(r.data || [])); }, []);

  useEffect(() => {
    const params = { per_page: 9 };
    Object.entries(filters).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined) return;
      if (k === "customs_cleared") params[k] = v === "yes";
      else if (k === "price_min" || k === "price_max") params[k] = Number(v);
      else params[k] = v;
    });
    api.get("/cars", { params }).then(r => { setResults(r.data.items || []); setCount(r.data.total || 0); });
  }, [filters]);

  const byType = useMemo(() => {
    const map = { brand: [], body: [], fuel: [], transmission: [], country: [], customs: [], euro: [] };
    options.forEach(o => { if (map[o.type]) map[o.type].push(o); });
    return map;
  }, [options]);

  const reset = () => setFilters({ brand: "", body_type: "", fuel_type: "", transmission: "", country_origin: "", customs_cleared: "", euro_standard: "", price_min: "", price_max: "" });

  const Group = ({ title, value, onChange, items }) => (
    <div>
      <h4 className="font-accent text-xs uppercase tracking-widest text-[var(--color-text-secondary)] mb-3">{title}</h4>
      <div className="flex flex-wrap gap-2">
        {items.map(it => {
          const v = it.value;
          const active = value === v;
          return (
            <button key={v} onClick={() => onChange(active ? "" : v)} className="px-4 py-2 rounded-full text-sm font-accent uppercase tracking-wider transition-all" style={{
              background: active ? "var(--color-primary)" : "transparent",
              color: active ? "#FFFFFF" : "var(--color-text-primary)",
              border: `1.5px solid ${active ? "var(--color-primary)" : "var(--color-border)"}`,
            }} data-testid={`cfg-${title}-${v}`}>
              {it.label || v}
            </button>
          );
        })}
      </div>
    </div>
  );

  const bg = settings?.bg_image_urls?.configurator;

  return (
    <div data-testid="configurator-page">
      {/* Hero */}
      <section className="relative py-16 md:py-24 section-on-dark">
        <div className="absolute inset-0">
          {bg && <img src={resolveImg(bg)} alt="" className="w-full h-full object-cover" />}
          <div className="absolute inset-0" style={{ background: "rgba(28,58,107,0.82)" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <SectionLabel light>{t(content, "configurator.label", "")}</SectionLabel>
          <h1 className="section-heading text-white mt-4">{t(content, "configurator.title", "")}</h1>
          <p className="text-white/75 mt-3 max-w-2xl">{t(content, "configurator.subtitle", "")}</p>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[400px_1fr] gap-10">
          <div className="space-y-7">
            <Group title="Марка" value={filters.brand} onChange={(v) => setFilters(f => ({...f, brand: v}))} items={byType.brand} />
            <Group title="Кузов" value={filters.body_type} onChange={(v) => setFilters(f => ({...f, body_type: v}))} items={byType.body} />
            <Group title="Топливо" value={filters.fuel_type} onChange={(v) => setFilters(f => ({...f, fuel_type: v}))} items={byType.fuel} />
            <Group title="Коробка" value={filters.transmission} onChange={(v) => setFilters(f => ({...f, transmission: v}))} items={byType.transmission} />
            <Group title="Страна" value={filters.country_origin} onChange={(v) => setFilters(f => ({...f, country_origin: v}))} items={byType.country} />
            <Group title="Растаможен" value={filters.customs_cleared} onChange={(v) => setFilters(f => ({...f, customs_cleared: v}))} items={byType.customs} />

            <div>
              <h4 className="font-accent text-xs uppercase tracking-widest text-[var(--color-text-secondary)] mb-3">Бюджет, ₽</h4>
              <div className="grid grid-cols-2 gap-2">
                <input className="field-input" type="number" placeholder="от" value={filters.price_min} onChange={e => setFilters(f => ({...f, price_min: e.target.value}))} data-testid="cfg-price-min" />
                <input className="field-input" type="number" placeholder="до" value={filters.price_max} onChange={e => setFilters(f => ({...f, price_max: e.target.value}))} data-testid="cfg-price-max" />
              </div>
            </div>

            <button onClick={reset} className="btn-secondary-cta w-full" data-testid="cfg-reset">Сбросить параметры</button>
          </div>

          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="font-accent text-xs uppercase tracking-widest text-[var(--color-text-secondary)]">Найдено</div>
                <div className="font-headings font-extrabold text-3xl text-[var(--color-primary)]" data-testid="cfg-count">{count}</div>
              </div>
              <Compass size={36} style={{ color: "var(--color-gold)" }} />
            </div>

            {results.length === 0 ? (
              <div className="text-center py-16 rounded-[var(--radius-lg)] border-2 border-dashed" style={{ borderColor: "var(--color-border)" }} data-testid="cfg-empty">
                <Compass size={42} className="mx-auto mb-4" style={{ color: "var(--color-text-secondary)" }} />
                <p className="text-[var(--color-text-secondary)] mb-5 max-w-md mx-auto">{t(content, "configurator.empty_text", "")}</p>
                <button onClick={() => setFeedbackOpen(true)} className="btn-primary-cta">{t(content, "configurator.empty_btn", "Оставить заявку")}</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {results.map(c => <CarCard key={c.id} car={c} />)}
              </div>
            )}
          </div>
        </div>
      </section>

      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} defaultMessage="Подбор автомобиля по параметрам конфигуратора" />
    </div>
  );
}
